<script setup lang="ts">
type EnumData = App.Base.Data.EnumData;

import type { HybridlyFormData } from '@/@types/global';

import { ButtonColor, ButtonVariant } from '@/hooks/useButtonClasses';

import Button from '@/components/button/Button.vue';
import CollegeApplicationFormFields from './CollegeApplicationFormFields.vue';
import DangerDialog from '@/components/dialog/DangerDialog.vue';
import Form from '@/components/form/Form.vue';
import FormFooter from '@/components/formFooter/FormFooter.vue';
import FormHeader from '@/components/formHeader/FormHeader.vue';

const props = defineProps<{
  isEditing?: boolean;
  form: HybridlyFormData<{
    institute_id: number;
    date_submitted: string;
    status: EnumData;
    intend_to_enroll: EnumData;
    has_waiver: EnumData;
    waiver: number;
    first_choice: boolean;
    award_meeting_held: boolean;
    early_action: boolean;
    early_decision: boolean;
    sent_hs_transcript: boolean;
    applied_to_honors_program: boolean;
    financial_aid_submitted_to_school: boolean;
  }>;
  returnUrl?: string;
  statuses: EnumData[];
  student: App.Students.Data.StudentData;
}>();

const isCancelDialogOpen = ref(false);
const isIntendToEnrollDialogOpen = ref(false);

const { onBackForward } = useBackForward();

// TODO: Fix intecepting the back button
onBackForward(() => {
  router.abort();
});

function handleToggleIntendToEnrollDialog() {
  isIntendToEnrollDialogOpen.value = !isIntendToEnrollDialogOpen.value;
}

function handleToggleCancelDialog() {
  if (props.form.isDirty) {
    isCancelDialogOpen.value = !isCancelDialogOpen.value;
    return;
  }
  handleRedirect();
}

function handleRedirect() {
  router.navigate({
    url:
      props.returnUrl || route('students.college-applications.index', { student: props.student.id })
  });
}

function handleFormSubmit() {
  if (props.form.fields.intend_to_enroll?.value && !props.form.initial.intend_to_enroll?.value) {
    handleToggleIntendToEnrollDialog();
  } else {
    props.form.submit();
  }
}
</script>

<template>
  <Form @submit.prevent="handleFormSubmit">
    <template #header>
      <FormHeader
        canClose
        :title="`${isEditing ? 'Edit' : 'Add'} a College Application`"
        :description="
          isEditing
            ? 'Use the form below to edit the college application.'
            : 'Fill out the form below add a new college application.'
        "
        @onClose="handleToggleCancelDialog"
      />
    </template>
    <div class="divide-y-slate-200 flex flex-col gap-y-8">
      <CollegeApplicationFormFields :form="form" :statuses="statuses" />
    </div>
    <template #footer>
      <FormFooter>
        <Button
          type="submit"
          :color="ButtonColor.primary"
          :isDisabled="form.processing || !form.isDirty"
          :isLoading="form.processing"
          :variant="ButtonVariant.solid"
          >Confirm</Button
        >
        <Button
          @click="handleToggleCancelDialog"
          :color="ButtonColor.slate"
          :variant="ButtonVariant.outlined"
          type="button"
        >
          Back to college applications
        </Button>
      </FormFooter>
    </template>
  </Form>
  <DangerDialog
    :title="`Cancel ${isEditing ? 'editing' : 'adding a new'} college application`"
    :message="`Are you sure you want to cancel ${
      isEditing ? 'editing' : 'adding'
    } this college application? All ${isEditing ? 'changes' : 'information'} will be lost.`"
    :isOpen="isCancelDialogOpen"
    cancelButtonLabel="Stay on this page"
    confirmButtonLabel="Go back to college applications"
    @onCancel="handleToggleCancelDialog"
    @onClose="handleToggleCancelDialog"
    @onConfirm="handleRedirect"
  />
  <DangerDialog
    title="Intend to Enroll confirmation"
    message="Setting the 'Intend to Enroll' status to 'Yes' for this college application will deactivate this status for any other college application that currently has it activated. Are you sure you want to proceed?"
    :isOpen="isIntendToEnrollDialogOpen"
    confirmButtonLabel="Confirm Intend to Enroll"
    @onCancel="handleToggleIntendToEnrollDialog"
    @onClose="handleToggleIntendToEnrollDialog"
    @onConfirm="form.submit"
  />
</template>
